import { useEffect, useState } from 'react';
import { useGetApiV1NotificationMobileApp } from '@/shared/api/generated/hooks/notificationMobileAppController/useGetApiV1NotificationMobileApp';
import type { EMobileNotificationStatus } from '@/shared/api/generated/types/EMobileNotificationStatus';
import type { MobileNotificationGridItemViewModel } from '@/shared/api/generated/types/MobileNotificationGridItemViewModel';
import dayjs from '@/shared/lib/dayjs';
import type {
  NotificationFilters,
  NotificationItem,
  NotificationStatus,
} from '../types/notification';

const PAGE_SIZE = 6;

export interface NotificationListResult {
  readonly data: readonly NotificationItem[];
  /** Carga inicial (sem dados em cache) — controla o skeleton da tabela. */
  readonly isLoading: boolean;
  /** Busca em andamento (debounce pendente ou refetch) — controla o indicador de busca. */
  readonly isFetching: boolean;
  readonly isError: boolean;
  readonly totalCount: number;
  readonly currentPage: number;
  readonly totalPages: number;
  readonly pageSize: number;
  readonly setPage: (page: number) => void;
  readonly refetch: () => void;
}

const STATUS_MAP: Record<EMobileNotificationStatus, NotificationStatus> = {
  1: 'draft',
  2: 'scheduled',
  3: 'processing',
  4: 'delivered',
  5: 'cancelled',
};

const STATUS_ID_MAP: Record<NotificationStatus, EMobileNotificationStatus> = {
  draft: 1,
  scheduled: 2,
  processing: 3,
  delivered: 4,
  cancelled: 5,
};

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(n => n[0] ?? '')
    .join('')
    .toUpperCase();
}

function toNotificationItem(item: MobileNotificationGridItemViewModel): NotificationItem {
  const senderName = item.createdBy ?? '';
  const sentAt = dayjs.utc(item.sentFullAt)?.format('DD/MM/YYYY HH:mm') ?? '';
  const createdAt = dayjs.utc(item.createdDate)?.format('DD/MM/YYYY HH:mm') ?? '';

  return {
    id: item.id?.toString() ?? '',
    sentAt,
    title: item.title ?? '',
    preview: item.message ?? '',
    classrooms: item.classStudentNames ?? [],
    studentCount: item.totalStudentsSent ?? 0,
    createdAt,
    sender: { name: senderName, initials: getInitials(senderName) },
    status: item.status !== undefined ? (STATUS_MAP[item.status] ?? 'processing') : 'processing',
    template: null,
    urlExternal: item.urlExternal ?? '',
  };
}

/**
 * Hook para listagem de notificações via API.
 *
 * @param filters - Filtros de busca aplicados na listagem
 * @returns Dados paginados de notificações com estado de loading/error
 */
const DEBOUNCE_MS = 2000;

export function useNotificationList(filters?: NotificationFilters): NotificationListResult {
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters]);

  const query = useGetApiV1NotificationMobileApp({
    PageNumber: currentPage,
    PageSize: PAGE_SIZE,
    ...(debouncedFilters?.search ? { SearchText: debouncedFilters.search } : {}),
    // Backend espera date-time; o filtro guarda apenas YYYY-MM-DD, então
    // estendemos o intervalo para o dia inteiro (início 00h, fim 23h59m59s).
    ...(debouncedFilters?.startDate ? { StartDate: `${debouncedFilters.startDate}T00:00:00` } : {}),
    ...(debouncedFilters?.endDate ? { EndDate: `${debouncedFilters.endDate}T23:59:59` } : {}),
    ...(debouncedFilters?.sentBy ? { CreatedById: Number(debouncedFilters.sentBy) } : {}),
    ...(debouncedFilters?.classroomId
      ? { ClassStudentIds: [Number(debouncedFilters.classroomId)] }
      : {}),
    ...(debouncedFilters?.statuses?.length
      ? { StatusNotification: debouncedFilters.statuses.map(s => STATUS_ID_MAP[s]) }
      : {}),
  });

  // O spec declara array mas o backend retorna envelope paginado (mesmo padrão de PersonList)
  const paginated = query.data as unknown as {
    data?: MobileNotificationGridItemViewModel[] | null;
    currentPage?: number;
    totalPages?: number;
    totalCount?: number;
  };

  const items = (paginated?.data ?? []).map(toNotificationItem);

  return {
    data: items,
    // Skeleton quando não há dados em cache para a query atual: carga inicial,
    // página ainda não carregada ou novo filtro. Refetch de fundo (dados já em
    // cache) atualiza sem piscar, pois isLoading = isPending && isFetching.
    isLoading: query.isLoading,
    isFetching: filters !== debouncedFilters || query.isFetching,
    isError: query.isError,
    totalCount: paginated?.totalCount ?? 0,
    currentPage: paginated?.currentPage ?? currentPage,
    totalPages: paginated?.totalPages ?? 1,
    pageSize: PAGE_SIZE,
    setPage: setCurrentPage,
    refetch: query.refetch,
  };
}

export interface NotificationFiltersResult {
  readonly filters: NotificationFilters;
  readonly updateFilters: (partial: Partial<NotificationFilters>) => void;
  readonly resetFilters: () => void;
}

const DEFAULT_FILTERS: NotificationFilters = {
  search: '',
  statuses: [],
  startDate: '',
  endDate: '',
  sentBy: '',
  sentByName: '',
  classroomId: '',
  classroomName: '',
};

/**
 * Hook para gerenciar o estado dos filtros da listagem de notificações.
 */
export function useNotificationFilters(): NotificationFiltersResult {
  const [filters, setFilters] = useState<NotificationFilters>(DEFAULT_FILTERS);

  const updateFilters = (partial: Partial<NotificationFilters>) => {
    setFilters(prev => ({ ...prev, ...partial }));
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  return { filters, updateFilters, resetFilters };
}
